import React from "react";
import { Link } from "react-router-dom";
import LegalPage from "./LegalPage";

// DRAFT — grounded in what the platform actually does as of Aug 2026, but not
// yet reviewed by counsel.

export default function PrivacyPolicy() {
  return (
    <LegalPage title="Privacy Policy" updated="October 19, 2026">
      <h2>Who we are</h2>
      <p>
        Feis.link ("we", "us") is an online registration and event-management platform
        for Irish dance events — feiseanna, grade examinations and oireachtasí —
        operated from 21432 Lynn Rd., Wheatley, Ontario N0P 2P0, Canada. Events on the platform are run by their own organizers; we process
        registration information on their behalf and for our own operation of the
        platform. Questions about this policy: <a href="mailto:info@feis.link">info@feis.link</a>.
      </p>

      <h2>What we collect</h2>
      <ul>
        <li>
          <b>Account information</b> — your name, email address and password (stored only
          as a cryptographic hash) when you create a parent, teacher or organizer account.
        </li>
        <li>
          <b>Dancer information</b> — the dancer's name, birth year, gender, school and
          competition entries, entered by a parent, guardian or teacher. Set-dance choices,
          speeds and similar competition details are part of the entry.
        </li>
        <li>
          <b>Mobile app</b> — if you use the Feis.link app, it keeps you signed in by storing
          a login token securely on your phone, and, if you allow notifications, it stores a
          notification ID for your phone so we can send results, entry reminders and payment
          updates. You can turn each kind off in the app or in your phone's settings. The app
          has no advertising, analytics or tracking.
        </li>
        <li>
          <b>Payment information</b> — payments are processed by Stripe. Your card details
          go directly to Stripe and are never seen or stored by our servers. We keep
          transaction records: amounts, dates, what was purchased, and receipts.
        </li>
        <li>
          <b>Technical information</b> — server logs (IP address, requests) and browser or
          app storage used to keep you signed in. We do not use advertising or analytics
          trackers.
        </li>
        <li>
          <b>Communications</b> — messages you send us, and the transactional email we
          send you (receipts, confirmations).
        </li>
      </ul>

      <h2>Children's information</h2>
      <p>
        Most dancers registered on Feis.link are minors. Dancer profiles are created and
        managed by an adult — a parent, guardian, or a teacher registering on behalf of
        their school — and we rely on that adult having the authority to provide the
        dancer's information. Minors do not hold their own Feis.link accounts. A parent or
        guardian may review or ask us to correct or delete their dancer's information at
        any time (see "Your choices and rights").
      </p>

      <h2>How we use information</h2>
      <ul>
        <li>Operating registration: eligibility checks, fee calculation, checkout, receipts.</li>
        <li>Running events: rosters, check-in, competitor numbers, scoring and tabulation.</li>
        <li>Publishing results (see the next section).</li>
        <li>Transactional communication — confirmations, receipts and service messages. We do not send marketing email.</li>
        <li>Support, troubleshooting, fraud prevention and legal compliance.</li>
      </ul>

      <h2>Competition results are public</h2>
      <p>
        Publishing results is inherent to competition: when an organizer publishes results,
        a dancer's name, school, competition and placement appear on public results pages,
        in printed results books, and on award materials. Judges' names appear on published
        results. If a published result needs correcting, contact the event organizer or{" "}
        <a href="mailto:info@feis.link">info@feis.link</a>.
      </p>

      <h2>Who we share information with</h2>
      <ul>
        <li>
          <b>Event organizers</b> — the organizer of an event you register for receives the
          registration information for that event: dancers, entries, schools and fee
          records. Organizers use it to run their event and are responsible for their own
          handling of it.
        </li>
        <li>
          <b>Service providers</b> — Stripe (payments), SendGrid (email delivery), Apple,
          Google and Expo (delivering app notifications), and our hosting and file-storage
          providers (DigitalOcean). They process data only to
          provide their service to us. Some providers store data outside Canada (for
          example, in the United States).
        </li>
        <li>
          <b>Legal</b> — if required by law, or to protect the platform and its users.
        </li>
      </ul>
      <p>We do not sell personal information, and we do not share it for advertising.</p>

      <h2>Retention and security</h2>
      <p>
        We keep registration and transaction records for as long as needed to run events,
        meet accounting and legal obligations, and maintain competition history. Data is
        encrypted in transit, passwords are stored hashed, and access to production systems
        is restricted. No system is perfectly secure; if a breach affects your information
        we will notify you as the law requires. When you delete your account, we remove your
        personal details as described below and keep the event records (entries, payments
        and results).
      </p>

      <h2>Your choices and rights</h2>
      <p>
        You can review and update account and dancer information from your account. You can
        delete your account yourself in the app (My account → Delete account) or at{" "}
        <Link to="/delete-account">feis.link/delete-account</Link>. Deleting your account
        removes your login, your name and email, and your phone's notifications; your
        dancers' entries and results stay with the events they took part in, with their
        names, as part of the organizers' records and the published results. You may
        ask us to access, correct or delete personal information by emailing{" "}
        <a href="mailto:info@feis.link">info@feis.link</a>; note that published competition
        results and records we must keep by law (for example, transaction records) may be
        retained or handled differently. We answer requests under Canada's federal privacy
        law (PIPEDA) and applicable provincial law; you may also complain to the Office of
        the Privacy Commissioner of Canada.
      </p>

      <h2>Changes to this policy</h2>
      <p>
        We may update this policy as the platform evolves. Material changes will be posted
        on this page with a new "Last updated" date.
      </p>

      <div className="legal-crosslink">
        See also our <Link to="/terms">Terms of Service</Link>.
      </div>
    </LegalPage>
  );
}
